/**
 * DemoClientPanel — owner-only practice workspace controls.
 *
 * Compact tile that lives in the Coach Roster header. Three states:
 *   1. Not seeded → "Set up demo workspace" button
 *   2. Seeded     → "Open client view" + "Reset" + helper text
 *   3. Loading    → spinner
 *
 * "Open client view" opens a NEW TAB pre-authenticated as the demo
 * parent — server sets the session_token cookie + returns the dashboard
 * URL. Coach's own session stays intact in the original tab so you can
 * Cmd+Tab between coach and client view to verify changes in real time.
 *
 * All Stripe charges fired against the demo client use Stripe TEST MODE
 * — no real money risk.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, ExternalLink, RotateCcw, FlaskConical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoClientPanel({ onSeeded }) {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // "seed" | "open" | "reset"
    const [demo, setDemo] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/owner/demo/status`, { withCredentials: true });
            setDemo(r.data?.seeded ? r.data : null);
        } catch {
            setDemo(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const seed = async () => {
        setBusy("seed");
        try {
            await axios.post(`${API}/owner/demo/seed`, {}, { withCredentials: true });
            toast.success("Demo workspace ready", {
                description: "Demo Parent + Demo Athlete are in your Members list. Stripe test card on file.",
            });
            await refresh();
            onSeeded?.();
        } catch (e) {
            toast.error("Couldn't seed demo workspace", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    const openClientView = async () => {
        if (!demo?.demo_parent_user_id) return;
        setBusy("open");
        try {
            // Mint the demo session token via the coach's authenticated request.
            // Backend safety-gates on is_demo=true so this can't be used to
            // impersonate a real member.
            const r = await axios.get(
                `${API}/owner/demo/${demo.demo_parent_user_id}/open`,
                { withCredentials: true },
            );
            const token = r.data?.token;
            if (!token) {
                toast.error("Couldn't get a demo session token");
                return;
            }
            // Open the new tab with the token in the URL — AuthContext picks
            // it up on first paint, stashes in sessionStorage, and strips it
            // from the URL. Coach's own session in this tab is untouched.
            const url = `${window.location.origin}/dashboard?demo_token=${encodeURIComponent(token)}`;
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                toast.error("Allow popups for this site to open the demo client view");
            } else {
                toast.success("Demo client view opened in a new tab", {
                    description: "Make changes here, then Cmd+Tab over and refresh to see them.",
                });
            }
        } catch (e) {
            toast.error("Couldn't open client view", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    const resetState = async () => {
        if (!demo?.demo_parent_user_id) return;
        if (!window.confirm("Wipe the demo client's practice data (charges, packs, messages) back to clean baseline?")) return;
        setBusy("reset");
        try {
            const r = await axios.post(
                `${API}/owner/demo/${demo.demo_parent_user_id}/reset`, {},
                { withCredentials: true },
            );
            const totalDeleted = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
            toast.success("Demo state reset", {
                description: totalDeleted ? `Cleared ${totalDeleted} practice rows.` : "Already clean.",
            });
            // Re-run seed to ensure the test card is still attached
            await axios.post(`${API}/owner/demo/seed`, {}, { withCredentials: true });
            await refresh();
        } catch (e) {
            toast.error("Couldn't reset", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return (
            <div className="border border-zinc-800 bg-zinc-950/40 rounded-lg px-4 py-3 inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Demo workspace</span>
            </div>
        );
    }

    return (
        <div
            data-testid="demo-client-panel"
            className="border border-amber-700/40 bg-amber-950/15 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap"
        >
            <FlaskConical className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 font-semibold">
                    Demo workspace · test mode
                </div>
                <div className="text-[11px] text-zinc-300 mt-0.5">
                    {demo
                        ? "Demo Parent is in your Members list. Stripe charges run on TEST cards — zero real money risk."
                        : "Spin up a mock client + athlete to practice the full coach + parent flows. Stripe stays in test mode."}
                </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
                {!demo ? (
                    <button
                        type="button"
                        data-testid="demo-seed"
                        onClick={seed}
                        disabled={busy !== null}
                        className="text-[10px] tracking-[0.2em] uppercase border border-amber-600 bg-amber-700/20 text-amber-200 hover:bg-amber-700/40 px-3 py-2 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                        {busy === "seed" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Set up demo workspace
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            data-testid="demo-open"
                            onClick={openClientView}
                            disabled={busy !== null}
                            className="text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 px-3 py-2 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {busy === "open" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                            Open client view
                        </button>
                        <button
                            type="button"
                            data-testid="demo-reset"
                            onClick={resetState}
                            disabled={busy !== null}
                            className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-200 hover:border-amber-500 hover:text-amber-300 px-3 py-2 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {busy === "reset" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                            Reset
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
